import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useUserEntries } from '../hooks/useDashboard';
import { Button } from '../components/ui';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import EmptyState from '../components/shared/EmptyState';

/**
 * My Entries Page
 *
 * Lists all user's competition entries with filtering.
 */
export default function MyEntries() {
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || 'all';

  const { entries, loading, error, totalCount, filteredCount } = useUserEntries({
    status: statusFilter,
  });

  const handleFilterChange = (value) => {
    if (value === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ status: value });
    }
  };

  // Count entries by status
  const statusCounts = useMemo(() => {
    const counts = { all: totalCount, active: 0, won: 0, lost: 0, refunded: 0 };
    // We'd need full data to compute these, for now use filteredCount
    return counts;
  }, [totalCount]);

  return (
    <div className="bg-midnight min-h-screen">
      {/* Header */}
      <div className="bg-midnight-light border-b border-cream/5">
        <div className="page-container py-8">
          {/* Back link */}
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 text-sm text-cream/60 hover:text-cream mb-4 transition-colors"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-center sm:text-left">
            <div>
              <h1 className="font-display font-bold text-2xl md:text-3xl text-cream">
                My Entries
              </h1>
              <p className="text-cream/60 mt-1">
                {totalCount} {totalCount === 1 ? 'entry' : 'entries'} total
              </p>
            </div>
            <Link to="/browse" className="self-center sm:self-auto">
              <Button>Browse Competitions</Button>
            </Link>
          </div>

          {/* Filter Tabs */}
          <div className="mt-6 -mb-px flex justify-center sm:justify-start">
            <Tabs value={statusFilter} onValueChange={handleFilterChange}>
              <TabsList className="bg-transparent border-none p-0 gap-0">
                <FilterTab value="all" label="All" active={statusFilter === 'all'} />
                <FilterTab value="active" label="Active" active={statusFilter === 'active'} />
                <FilterTab value="won" label="Won" active={statusFilter === 'won'} />
                <FilterTab value="lost" label="Didn't Win" active={statusFilter === 'lost'} />
                <FilterTab value="refunded" label="Refunded" active={statusFilter === 'refunded'} />
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-container py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-error">Error loading entries</p>
          </div>
        ) : entries.length === 0 ? (
          <EmptyState
            icon={TicketIcon}
            title={statusFilter === 'all' ? 'No entries yet' : `No ${statusFilter} entries`}
            description={
              statusFilter === 'all'
                ? "You haven't entered any competitions yet. Browse available competitions to get started!"
                : `You don't have any ${statusFilter} entries.`
            }
            actionLabel="Browse Competitions"
            onAction={() => window.location.href = '/browse'}
          />
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <EntryCard key={entry.id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterTab({ value, label, active }) {
  return (
    <TabsTrigger
      value={value}
      className={`px-4 py-3 rounded-none border-b-2 ${
        active
          ? 'border-primary text-cream bg-transparent'
          : 'border-transparent text-cream/60 hover:text-cream bg-transparent'
      }`}
    >
      {label}
    </TabsTrigger>
  );
}

function EntryCard({ entry }) {
  const { raffle } = entry;
  const photo = raffle?.photos?.[0];

  return (
    <Link
      to={`/competition/${entry.raffleId}`}
      className="group flex items-center gap-4 bg-midnight-light/50 hover:bg-midnight-light rounded-xl p-4 border border-cream/5 hover:border-primary/20 transition-all"
    >
      {/* Photo */}
      <div className="w-20 h-20 rounded-lg overflow-hidden shrink-0 bg-midnight-lighter">
        {photo ? (
          <img
            src={photo}
            alt={raffle?.title || 'Competition'}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TicketIcon className="w-8 h-8 text-cream/20" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-cream truncate group-hover:text-primary transition-colors">
              {raffle?.title || 'Loading...'}
            </h3>
            <p className="text-sm text-cream/50 mt-0.5">
              {entry.ticketCount} {entry.ticketCount === 1 ? 'entry' : 'entries'} • {formatCurrency(entry.totalPaid)}
            </p>
          </div>
          <EntryStatusBadge entry={entry} />
        </div>

        {/* Ticket Numbers */}
        {entry.ticketNumbers?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {entry.ticketNumbers.slice(0, 5).map((num) => (
              <span
                key={num}
                className="text-xs font-mono bg-midnight-lighter px-2 py-1 rounded text-cream/60"
              >
                #{num}
              </span>
            ))}
            {entry.ticketNumbers.length > 5 && (
              <span className="text-xs text-cream/40">
                +{entry.ticketNumbers.length - 5} more
              </span>
            )}
          </div>
        )}

        {/* Entry date */}
        <p className="text-xs text-cream/40 mt-2">
          Entered {formatDate(entry.createdAt)}
        </p>
      </div>

      {/* Arrow */}
      <ChevronRightIcon className="w-5 h-5 text-cream/30 group-hover:text-primary shrink-0" />
    </Link>
  );
}

function EntryStatusBadge({ entry }) {
  if (entry.isWinner) {
    return <Badge variant="gold" size="sm">Won</Badge>;
  }

  if (entry.refundStatus === 'completed') {
    return <Badge variant="default" size="sm">Refunded</Badge>;
  }

  if (entry.raffleStatus === 'completed') {
    return <Badge variant="default" size="sm">Didn't Win</Badge>;
  }

  if (entry.raffleStatus === 'active') {
    return <Badge variant="active" size="sm">Active</Badge>;
  }

  if (entry.raffleStatus === 'cancelled') {
    return <Badge variant="warning" size="sm">Cancelled</Badge>;
  }

  return <Badge variant="default" size="sm">Pending</Badge>;
}

// Helpers
function formatCurrency(cents) {
  if (!cents) return '€0';
  return `€${(cents / 100).toFixed(2)}`;
}

function formatDate(timestamp) {
  if (!timestamp) return 'Unknown';
  const date = timestamp.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);
  return date.toLocaleDateString('en-IE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// Icons
function TicketIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  );
}

function ChevronRightIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

function ArrowLeftIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}
